import { Alert, Modal, Table } from 'antd';
import { useReservedOrders } from '@/hooks/useStockLookup';
import {
  canOpenReservedOrder,
  reservedOrderColumns,
  reservedOrderHref,
} from './parts/columns';

interface Props {
  open: boolean;
  variant: {
    variant_id: string;
    product_name: string;
    unit_reserved: number;
  } | null;
  warehouseId?: string;
  /** TCR-2572 — chi nhánh đang lọc trên màn (khi chưa chọn kho cụ thể). */
  storeId?: string;
  onClose: () => void;
}

/**
 * TCR-1954 — popup khi bấm vào con số ở cột "SL đã đặt chưa giao".
 *
 * Con số trên cột lấy từ read-model tồn kho (theo kho), còn danh sách đơn dựng từ
 * `inventory_reservations`. Hai nguồn có thể lệch vì một phần reservation KHÔNG gắn kho
 * (đo 2026-09-23 trên DB demo: 34/443), và vì đơn ngoài phạm vi xem của người dùng bị
 * lọc ra. Khi lệch thì NÓI RA, đừng im lặng và cũng đừng sửa con số trên cột cho khớp.
 *
 * Fix round 2 — BA ghi chú TÁCH BẠCH, không gộp làm một:
 * 1. Lệch số (`reserved_total` ≠ `listed_total`) — nguyên nhân là phạm vi xem đơn, phần
 *    giữ hàng chưa gắn kho, hoặc nguồn giữ hàng không phải đơn.
 * 2. Danh sách đơn bị CẮT ở 100 (`order_list_truncated`) — đây KHÔNG phải vấn đề quyền,
 *    gộp vào ghi chú 1 là đổ oan nguyên nhân và người dùng không biết đường xử lý.
 * 3. Số phiếu giữ hàng vượt trần quét (`reservation_scan_truncated`) — chính con số
 *    `reserved_total` cũng đang thiếu, phải nói rõ.
 *
 * Fix round 3 (Residual B) — ghi chú "phần giữ hàng bị loại vì chưa gắn kho"
 * (`unattributed_excluded`) phải ĐỘC LẬP với ghi chú lệch số. Phần bị loại đã bị TRỪ khỏi
 * `reserved_total` ngay ở BE, nên riêng nó KHÔNG BAO GIỜ tạo ra khoảng lệch: treo nó bên
 * trong `hasGap` là để nó không bao giờ hiện đúng lúc cần — trái hẳn tinh thần §7.1 (lệch
 * thì phải nói ra). Khi CẢ HAI cùng xảy ra thì gộp làm MỘT alert duy nhất: câu lệch số
 * trước, câu phần bị loại nối ngay sau — không lặp lại cùng một ý ở hai chỗ.
 *
 * TCR-1990 — câu lệch số trước đây LUÔN mở đầu bằng "đơn ngoài phạm vi xem của bạn", kể
 * cả khi người xem là admin và phần chênh thực chất là đơn sàn TikTok (gọi nhầm là "phiếu
 * chuyển kho hoặc giữ tay") — QA đọc thành "bị chặn dù là TK admin". Giờ đơn sàn được liệt
 * kê thành dòng, và câu lệch số chỉ kể những nguyên nhân CÓ THẬT, kèm con số từng phần.
 */
const NON_ORDER_SOURCE_LABELS: Record<string, string> = {
  stock_transfer: 'phiếu chuyển kho',
  manual: 'giữ hàng thủ công',
  sales_invoice: 'hoá đơn bán hàng',
};

function vn(n: number): string {
  return n.toLocaleString('vi-VN');
}

export function ReservedOrdersModal({
  open,
  variant,
  warehouseId,
  storeId,
  onClose,
}: Props) {
  const { data, isLoading } = useReservedOrders(
    open ? (variant?.variant_id ?? null) : null,
    warehouseId,
    storeId,
  );

  const meta = data?.meta;
  const reservedTotal = meta?.reserved_total ?? 0;
  const listedTotal = meta?.listed_total ?? 0;
  const nonOrderTotal = meta?.non_order_reserved_total ?? 0;
  const hasGap = !isLoading && Boolean(data) && listedTotal !== reservedTotal;
  const orderListTruncated = !isLoading && Boolean(meta?.order_list_truncated);
  const scanTruncated = !isLoading && Boolean(meta?.reservation_scan_truncated);

  // Chỉ ghép câu giải thích cho nguyên nhân THẬT SỰ có (con số > 0) — liệt kê mọi nguyên
  // nhân "cho đủ" làm người dùng không biết cái nào đang áp dụng cho mình.
  const ecomHidden = meta?.ecom_order_hidden_total ?? 0;
  // BE cũ chưa trả `order_hidden_total` → suy ra từ phần còn lại sau khi trừ các nguồn đã biết.
  const orderHidden =
    meta?.order_hidden_total ??
    Math.max(reservedTotal - listedTotal - nonOrderTotal - ecomHidden, 0);
  const nonOrderByType = meta?.non_order_reserved_by_type;
  const nonOrderReasons =
    nonOrderByType && Object.keys(nonOrderByType).length > 0
      ? Object.entries(nonOrderByType)
          .filter(([, qty]) => qty > 0)
          .map(
            ([type, qty]) =>
              `${vn(qty)} thuộc ${NON_ORDER_SOURCE_LABELS[type] ?? 'nguồn giữ hàng khác'}`,
          )
      : nonOrderTotal > 0
        ? [`${vn(nonOrderTotal)} là phần giữ hàng không thuộc đơn hàng`]
        : [];
  const gapReasons = [
    ...(orderHidden > 0
      ? [
          orderListTruncated
            ? `${vn(orderHidden)} thuộc đơn chưa hiển thị hết hoặc ngoài phạm vi xem của bạn`
            : `${vn(orderHidden)} thuộc đơn ngoài phạm vi xem của bạn`,
        ]
      : []),
    ...(ecomHidden > 0
      ? [`${vn(ecomHidden)} thuộc đơn sàn TMĐT (bạn chưa có quyền xem đơn sàn)`]
      : []),
    ...nonOrderReasons,
  ].join('; ');

  // Phần bị loại vì chưa gắn kho KHÔNG nằm trong `gapReasons` nữa: nó đã bị trừ khỏi
  // `reserved_total` nên không phải nguyên nhân của khoảng lệch, và phải nói được cả khi
  // không lệch. Một câu duy nhất, dùng chung cho hai tình huống dưới đây.
  const unattributedExcluded =
    !isLoading && Boolean(meta?.unattributed_excluded);
  const unattributedClause =
    'có phần giữ hàng chưa gắn kho nên không quy được về chi nhánh — phần này đã bị loại khỏi cả tổng đang giữ lẫn danh sách bên dưới';

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={900}
      title={`Đơn đang giữ hàng — ${variant?.product_name ?? ''}`}
    >
      {hasGap && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={`Đang giữ ${vn(reservedTotal)}, danh sách dưới đây thể hiện ${vn(listedTotal)}.${
            gapReasons ? ` Phần chênh lệch không liệt kê được: ${gapReasons}.` : ''
          }${
            unattributedExcluded ? ` Ngoài ra, ${unattributedClause}.` : ''
          }`}
        />
      )}
      {/* Không lệch số nhưng vẫn có phần bị loại → phải có alert riêng, nếu không người
          dùng không được báo gì cả. Có lệch số thì câu này đã nằm trong alert trên rồi,
          không dựng thêm alert thứ hai để khỏi lặp nguyên văn một câu ở hai chỗ. */}
      {!hasGap && unattributedExcluded && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={`Lưu ý: ${unattributedClause}.`}
        />
      )}
      {orderListTruncated && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={`Mặt hàng này đang bị ${(meta?.matched_order_count ?? 0).toLocaleString('vi-VN')} đơn giữ hàng, danh sách chỉ hiển thị ${(meta?.listed_order_count ?? 0).toLocaleString('vi-VN')} đơn đầu tiên. Lọc theo kho để thu hẹp danh sách.`}
        />
      )}
      {scanTruncated && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="Mặt hàng này có quá nhiều phiếu giữ hàng đang hiệu lực nên hệ thống chỉ quét được một phần — kể cả tổng đang giữ ở trên cũng chưa đầy đủ."
        />
      )}
      <Table
        size="small"
        bordered
        rowKey={(row) => `${row.source_type}:${row.order_id}`}
        loading={isLoading}
        columns={reservedOrderColumns}
        dataSource={data?.data ?? []}
        pagination={false}
        scroll={{ y: 360 }}
        onRow={(row) =>
          canOpenReservedOrder(row)
            ? {
                style: { cursor: 'pointer' },
                onClick: () => window.open(reservedOrderHref(row), '_blank'),
              }
            : {}
        }
      />
    </Modal>
  );
}
